import { Plus } from 'lucide-react'
import { validateName } from '@super/node-graph-ui'
import { Button } from '@/components/ui/button'
import {
  ACTION_ROW_TYPE_OPTIONS,
  ActionRowEditor,
  type ActionRow,
  type ActionRowType,
  type PushOption,
  type PushFieldDropdownItems,
  type TransferSettings,
} from '@/components/actions/ActionEditor'
import { ActionBasicInfoFields } from '@/components/actions/ActionBasicInfoFields'

export interface ActionCatalogFormProps {
  // Identity
  /** Routed to the ActionRowEditor as its `actionGroupId` — the catalog
   *  layer uses it to key per-row mention state. */
  actionGroupId: string

  // Controlled values
  name: string
  description: string
  rows: ActionRow[]
  transfer: TransferSettings

  // Change handlers
  onNameChange: (next: string) => void
  onDescriptionChange: (next: string) => void
  onRowChange: (rowId: string, patch: Partial<ActionRow>) => void
  onRowAppend?: () => void
  onRowRemove?: (rowId: string) => void
  onTransferChange?: (next: TransferSettings) => void

  // Validation UI state. Callers compute these from
  // `validateActionCatalogForm` and pass them back so the form renders the
  // same red copy for every call site.
  nameError: boolean
  targetErrors: ReadonlySet<string>

  // Config
  /** Row types the author cannot add. The Action-node inspector excludes
   *  TRANSFER + PHONE_CALL; the main catalog page excludes PHONE_CALL only. */
  disallowedTypesBase: ReadonlySet<ActionRowType>
  /** True in multi-row contexts (node-agent inspector); false in single-row
   *  contexts (main action detail page). Drives the Add-action button + the
   *  per-row index gutter. */
  canAddRows: boolean
  /** Narrow-container layout for the ActionRowEditor rows. The inline
   *  inspector always wants this; the main page's wide form does not. */
  isPanel?: boolean

  // Ambient data the row editor needs
  pushOptions: PushOption[]
  pushFieldDropdownItems: PushFieldDropdownItems
  pushFieldFailedSources: Set<string>

  // Admin-side ergonomics
  /** When true, the Name field grabs focus on mount. Used by the
   *  create-new flow in the node-agent inspector. */
  autoFocusName?: boolean
  /** Optional ref so the parent can scroll to / focus the target section
   *  when validation fails. */
  targetSectionRef?: React.RefObject<HTMLDivElement | null>
  /** Optional ref so the parent can focus the name field. */
  nameInputRef?: React.RefObject<HTMLInputElement | null>
}

/**
 * The one authoring form every "edit a WorkflowAction / NodeActionGroup"
 * surface shares: Name + Description + row list (via ActionRowEditor) +
 * Transfer-specific settings when a row is TRANSFER. Pure controlled
 * component — state and save behavior live in the caller.
 */
export function ActionCatalogForm({
  actionGroupId,
  name,
  description,
  rows,
  transfer,
  onNameChange,
  onDescriptionChange,
  onRowChange,
  onRowAppend,
  onRowRemove,
  nameError,
  targetErrors,
  disallowedTypesBase,
  canAddRows,
  isPanel,
  pushOptions,
  pushFieldDropdownItems,
  pushFieldFailedSources,
  autoFocusName,
  targetSectionRef,
  nameInputRef,
}: ActionCatalogFormProps) {
  return (
    <div className="space-y-4">
      <ActionBasicInfoFields
        ref={nameInputRef}
        name={name}
        description={description}
        onNameChange={onNameChange}
        onDescriptionChange={onDescriptionChange}
        nameError={nameError}
        autoFocusName={autoFocusName}
      />

      <div className="space-y-2" ref={targetSectionRef}>
        {rows.map((row, index) => {
          // Prevent the same row type twice in a group AND respect the
          // caller's base exclusion list (PHONE_CALL always, TRANSFER on
          // Action nodes).
          const disallowed = new Set<ActionRowType>(disallowedTypesBase)
          for (const other of rows) {
            if (other.id !== row.id) disallowed.add(other.type)
          }
          return (
            <div key={row.id} className="rounded-md border border-border p-2">
              <ActionRowEditor
                row={row}
                index={index}
                actionGroupId={actionGroupId}
                actionTypes={ACTION_ROW_TYPE_OPTIONS}
                pushOptions={pushOptions}
                pushFieldDropdownItems={pushFieldDropdownItems}
                pushFieldFailedSources={pushFieldFailedSources}
                onChange={patch => onRowChange(row.id, patch)}
                onRemove={() => onRowRemove?.(row.id)}
                canRemove={canAddRows && rows.length > 1}
                disallowedTypes={disallowed}
                targetError={targetErrors.has(row.id)}
                transfer={row.type === 'TRANSFER' ? transfer : undefined}
                isPanel={isPanel}
                showIndex={canAddRows && rows.length > 1}
              />
            </div>
          )
        })}
      </div>

      {canAddRows && onRowAppend && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRowAppend}
          className="w-full"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add action
        </Button>
      )}
    </div>
  )
}

export interface ValidateActionCatalogFormInput {
  name: string
  rows: ActionRow[]
}

export interface ValidateActionCatalogFormResult {
  nameError: boolean
  targetErrors: ReadonlySet<string>
  issues: string[]
}

/**
 * Pure validation of the catalog form state. Delegates Name to the shared
 * `validateName` so the standalone page, the inline inspector, and the
 * Transfer edit body all enforce the same rule.
 */
export function validateActionCatalogForm({
  name,
  rows,
}: ValidateActionCatalogFormInput): ValidateActionCatalogFormResult {
  const issues: string[] = []
  const nameResult = validateName(name)
  const nameError = nameResult.nameError
  if (nameResult.issue) issues.push(nameResult.issue)
  const targetErrors = new Set<string>()
  for (const row of rows) {
    const empty =
      row.type === 'PUSH_INTEGRATION'
        ? (row.pushTargets ?? []).length === 0
        : !row.target.trim()
    if (empty) targetErrors.add(row.id)
  }
  if (targetErrors.size > 0) issues.push('Set a target for every action')
  return { nameError, targetErrors, issues }
}
